import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { houseLoginUrl } from '../shell/houseLogin';

interface ProtectedRouteProps {
  children: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Signing in happens at the house. On a product host an unauthenticated
  // visit leaves for `app.<domain>/login?next=<here>` rather than rendering
  // this host's own login form; the house sends them back once signed in.
  // Null on the house itself and on a single-label host, where the router
  // redirect below is the whole story. See `shell/houseLogin.ts`.
  const bounce =
    !loading && !isAuthenticated
      ? houseLoginUrl(window.location.hostname, window.location.origin, window.location.href)
      : null;

  useEffect(() => {
    if (bounce) window.location.replace(bounce);
  }, [bounce]);

  if (loading || bounce) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
